import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom-v5-compat'

import { useAuthenticatedUser } from '@sparkai/auth'
import { UserRoles } from '@sparkai/shared-types'
import { AppsPaths, getAppPort } from '@sparkai/utils'

import { ShareLinkAccessState } from './ShareLinkAccessState'

export function Redirector() {
  const { profile } = useAuthenticatedUser()
  const location = useLocation()

  const hasRedirected = useRef(false)

  useEffect(() => {
    if (hasRedirected.current) return
    const returnTo = location.state?.returnTo

    if (returnTo) {
      const canonicalSharePath = getCanonicalSharePath(returnTo)
      const redirectUrl = canonicalSharePath
        ? `${globalThis.location.origin}${canonicalSharePath}`
        : buildRedirectUrl('/web/', returnTo)
      globalThis.location.href = redirectUrl
      hasRedirected.current = true

      return
    }

    if (!profile?.role) return

    const redirectUrl = getRedirectUrlByRole(profile.role)

    globalThis.location.href = redirectUrl
    hasRedirected.current = true
  }, [location.state?.returnTo, profile?.role])

  return <ShareLinkAccessState state='loading' />
}

const buildRedirectUrl = (path: AppsPaths, returnTo?: string) => {
  const parsedReturnTo = returnTo ? returnTo.replace(/^\//, '') : ''
  if (import.meta.env.DEV && location.hostname !== 'spark.local') {
    const origin = `${globalThis.location.protocol}//${globalThis.location.hostname}`

    const url = `${origin}:${getAppPort(path)}${path}${parsedReturnTo}`
    return url
  }

  return `${globalThis.location.origin}${path}${parsedReturnTo}`
}

export const getCanonicalSharePath = (returnTo: string) => {
  if (returnTo.startsWith('/engagement/') || returnTo.startsWith('/task/')) return returnTo

  const engagementMatch = /^\/(?:admin|customer)\/engagement\/([^/?#]+)\/?([?#].*)?$/.exec(returnTo)
  if (engagementMatch) return `/engagement/${engagementMatch[1]}${engagementMatch[2] ?? ''}`
}

export const getRedirectUrlByRole = (role: UserRoles) => {
  switch (role) {
    case 'workforce_manager':
    case 'admin':
      return buildRedirectUrl('/admin/')
    case 'applicant':
    case 'operator':
    case 'trainee':
      return buildRedirectUrl('/ms/')
    case 'customer':
    case 'account_owner':
      return buildRedirectUrl('/customer/')
    default:
      return buildRedirectUrl('/web/')
  }
}
